import { CommonModule, DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal
} from '@angular/core';
import type { AfterViewInit, OnDestroy } from '@angular/core';
import App from '@app/app';
import { downgradeComponent } from '@angular/upgrade/static';
import { LtTranslatePipe } from '@app/pipes/lt-translate.pipe';
import { LtIconComponent } from '@app/components/core/lt-icon/lt-icon.component';

type ShoppingFlowBarCtaIcon = 'arrow' | 'lock' | 'none';
type ShoppingFlowBarTranslateParams = Record<string, string> | undefined;

@Component({
  selector: 'lt-shopping-flow-bar',
  standalone: true,
  templateUrl: './shopping-flow-bar.component.html',
  styleUrl: './shopping-flow-bar.component.scss',
  imports: [CommonModule, LtTranslatePipe, LtIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShoppingFlowBarComponent implements AfterViewInit, OnDestroy {
  private static nextInstanceId = 0;
  private static updateSequence = 0;
  private static measuredHeights = new Map<number, { height: number; sequence: number }>();

  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private readonly document = inject(DOCUMENT);
  private readonly instanceId = ShoppingFlowBarComponent.nextInstanceId++;
  private readonly measuredHeight = signal<number | undefined>(undefined);
  private resizeObserver?: ResizeObserver;

  headline = input('');
  headlineParams = input<ShoppingFlowBarTranslateParams>(undefined);
  subheadline = input('');
  subheadlineParams = input<ShoppingFlowBarTranslateParams>(undefined);
  subheadlineValue = input('');
  ctaLabel = input.required<string>();
  ctaLabelParams = input<ShoppingFlowBarTranslateParams>(undefined);
  ctaIcon = input<ShoppingFlowBarCtaIcon>('arrow');
  disabled = input(false);
  inactive = input(false);

  continueClicked = output();

  isDisabled = computed(() => this.disabled());
  isInactive = computed(() => this.inactive());

  constructor() {
    effect(() => {
      const height = this.measuredHeight();

      if (height == null) {
        ShoppingFlowBarComponent.measuredHeights.delete(this.instanceId);
      } else {
        ShoppingFlowBarComponent.measuredHeights.set(this.instanceId, {
          height,
          sequence: ++ShoppingFlowBarComponent.updateSequence
        });
      }

      ShoppingFlowBarComponent.syncGlobalOffset(this.document);
    });
  }

  ngAfterViewInit(): void {
    this.updatePageOffset();

    if (typeof ResizeObserver !== 'function') {
      return;
    }

    this.resizeObserver = new ResizeObserver((entries) => {
      const blockSize = entries[0]?.borderBoxSize?.[0]?.blockSize;

      this.updatePageOffset(blockSize);
    });
    this.resizeObserver.observe(this.hostElement.nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    ShoppingFlowBarComponent.measuredHeights.delete(this.instanceId);
    ShoppingFlowBarComponent.syncGlobalOffset(this.document);
  }

  protected onContinueClick() {
    this.continueClicked.emit();
  }

  private updatePageOffset(height = this.hostElement.nativeElement.getBoundingClientRect().height): void {
    this.measuredHeight.set(Math.ceil(height));
  }

  private static syncGlobalOffset(document: Document): void {
    if (ShoppingFlowBarComponent.measuredHeights.size === 0) {
      document.body.classList.remove('has-shopping-flow-bar');
      document.documentElement.style.removeProperty('--lt-shopping-flow-bar-height');
      return;
    }

    const latestMeasurement = [...ShoppingFlowBarComponent.measuredHeights.values()].sort(
      (left, right) => right.sequence - left.sequence
    )[0];

    document.body.classList.add('has-shopping-flow-bar');
    document.documentElement.style.setProperty('--lt-shopping-flow-bar-height', `${latestMeasurement.height}px`);
  }
}

App.directive('ltShoppingFlowBar', downgradeComponent({ component: ShoppingFlowBarComponent }));
